/**
 * What: Renders the action footer with Preflight, Credit Summary breakdown, and Call Request in Polaris.
 * Transport / data-flow: Action bar bound to preflight state and run triggers.
 *
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { ActionButton, type ActionButtonLifecycle } from '../ActionButton'
import type { PageActionName } from '../../page-declaration'
import type { page } from '../../pages/study-builder.page'

export function StudySetupActionBar({
  canSubmit,
  readiness,
  lifecycle,
  onSubmit,
}: {
  /** The page's final gate: the Worker's canSubmit lowered for in-flight browser facts. Controls Submit only. */
  canSubmit: boolean
  /** The Worker's finished words for the current state: a heading and/or ONE sentence. A grey Submit always carries one
   * help sentence (the instruction to fill the starred fields, or "Fix the problems above…") and no heading; `heading` is
   * null then. The renderer draws each line only when it is non-null. */
  readiness: { heading: string | null; help: string | null }
  /** Each button's loading/disabled comes from the page lifecycle, not from the editor. */
  lifecycle: ActionButtonLifecycle<PageActionName<typeof page>>
  onSubmit: () => void
}) {
  const copy = shopifyComponentCopy.studyBuilderReadiness
  return (
    <s-section>
      <s-stack direction="block" gap="large">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="block" gap="small-100">
            {readiness.heading ? <s-text type="strong">{readiness.heading}</s-text> : null}
            {readiness.help ? <s-text color="subdued">{readiness.help}</s-text> : null}
          </s-stack>
          <s-stack direction="inline" gap="base">
            <ActionButton
              lifecycle={lifecycle}
              action="run-study"
              variant="primary"
              onClick={onSubmit}
              disabled={!canSubmit}
            >
              {copy.submit}
            </ActionButton>
          </s-stack>
        </s-stack>
      </s-stack>
    </s-section>
  )
}
